import React, { useEffect, useState } from 'react';
import { UserMinusIcons } from '../../../../../../icons/icons';
import { NameEmailAvatar } from '../../../../../../ui';
import { TrashIcon } from '../../../../../../ui/icons/icons';
import {
  IUserDisplay,
  PermissionType,
} from '../../../../../../utils/api_service/access_control';

export interface ISelectedUserPermissionsProps {
  title: string;
  users: IUserDisplay[] | undefined;
  onPermissionChange?: (userId: string, permissions: PermissionType[]) => void;
  readOnly?: boolean;
  onDelete?: (user: IUserDisplay) => void; // << Accept user object!
}

export const SelectedUserPermissions: React.FC<ISelectedUserPermissionsProps> =
  ({ title, users, onPermissionChange, readOnly = false, onDelete }) => {
    const [permissions, setPermissions] = useState<
      Record<string, PermissionType[]>
    >({});

    useEffect(() => {
      if (users) {
        const newPerms: Record<string, PermissionType[]> = {};
        users.forEach((user) => {
          newPerms[user.id] = user.permissions || [];
        });
        setPermissions(newPerms);
      }
    }, [users]);

    const handleCheckbox = (userId: string, permType: PermissionType) => {
      setPermissions((prev) => {
        const prevPerms = prev[userId] || [];
        let updated: PermissionType[];
        if (prevPerms.includes(permType)) {
          updated = prevPerms.filter((p) => p !== permType);
        } else {
          updated = [...prevPerms, permType];
        }
        onPermissionChange?.(userId, updated);
        return { ...prev, [userId]: updated };
      });
    };

    return (
      <div>
        <h2 className='text-base font-semibold mb-3 text-slate-900 sm:text-lg'>
          { title }
        </h2>
        <div className='space-y-2 sm:hidden'>
          { users?.map((user) => (
            <div
                key={user.id || user.email}
                className='rounded-lg border border-slate-100 bg-slate-50/70 p-3'
            >
              <div className='mb-3'>
                <NameEmailAvatar
                    name={`${user.firstName} ${user.lastName}`}
                    email={user.email}
                    avatar=''
                />
              </div>
              <div className='grid grid-cols-3 gap-2'>
                { (['read', 'write', 'delete'] as PermissionType[]).map(
                  (permType) => (
                    <label
                        key={permType}
                        className='flex min-h-10 items-center justify-between rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold capitalize text-slate-600'
                    >
                      { permType }
                      <input
                          type='checkbox'
                          checked={
                            permissions[user.id]?.includes(permType) ?? false
                          }
                          onChange={() => handleCheckbox(user.id, permType)}
                          className='h-4 w-4 rounded border-gray-300 text-green-600'
                          disabled={readOnly}
                      />
                    </label>
                  ),
                ) }
              </div>
              { readOnly && onDelete && (
                <button
                    onClick={() => onDelete(user)}
                    className='mt-3 inline-flex min-h-9 w-full items-center justify-center rounded-md border border-red-100 bg-red-50 px-3 text-sm font-semibold text-red-600 hover:bg-red-100'
                    title='Delete Permission'
                >
                  Remove Access
                </button>
              ) }
            </div>
          )) }
        </div>
        <div className='hidden overflow-x-auto sm:block'>
          <table className='w-full min-w-[560px] table-auto text-left border-collapse'>
            <thead>
              <tr className='bg-gray-50'>
                <th className='px-4 py-3 text-left'>User</th>
                <th className='px-4 py-3 w-32 text-left'>Read</th>
                <th className='px-4 py-3 w-32 text-left'>Write</th>
                <th className='px-4 py-3 w-32 text-left'>Delete</th>
                { readOnly && <th className='px-4 py-3 w-10 text-left'></th> }
              </tr>
            </thead>
            <tbody>
              { users?.map((user, i) => (
                <tr
                    key={user.id || user.email}
                    className={i % 2 === 0 ? 'bg-gray-100' : 'bg-white'}
                >
                  <td className='px-4 py-3 text-left'>
                    <NameEmailAvatar
                        name={`${user.firstName} ${user.lastName}`}
                        email={user.email}
                        avatar=''
                    />
                  </td>
                  { (['read', 'write', 'delete'] as PermissionType[]).map(
                    (permType) => (
                      <td key={permType} className='px-4 py-3 text-left'>
                        <div className='flex items-center'>
                          <input
                              type='checkbox'
                              checked={
                                permissions[user.id]?.includes(permType) ?? false
                              }
                              onChange={() => handleCheckbox(user.id, permType)}
                              className='h-4 w-4 text-green-600 border-gray-300 rounded'
                              disabled={readOnly}
                          />
                        </div>
                      </td>
                    ),
                  ) }
                  { readOnly && onDelete && (
                    <td className='px-4 py-3 text-left'>
                      <button
                          onClick={() => onDelete(user)}
                          className='p-1 text-red-500 hover:text-red-700 hover:bg-red-500/20 rounded-full'
                          title='Delete Permission'
                      >
                        <UserMinusIcons className='w-6 h-6' />
                      </button>
                    </td>
                  ) }
                </tr>
              )) }
            </tbody>
          </table>
        </div>
      </div>
    );
  };
